import { useEffect, useState } from "react";
import { Modal } from "semantic-ui-react";

import { useSelectedLocation } from "components";
import { createFormHelper } from "lib/Form";
import { PrivateKeyModal } from "modules/KeyLockersDashboard/components/CreateEditKeyLockerModal/components";
import { CreateEditKeyLockerForm } from "modules/KeyLockersDashboard/components/CreateEditKeyLockerModal/components/CreateEditKeyLockerForm";
import "modules/KeyLockersDashboard/components/CreateEditKeyLockerModal/CreateEditKeyLockerModal.scss";
import { useCreateKeyLockerMutation, useUpdateKeyLockerMutation } from "modules/KeyLockersDashboard/components/CreateEditKeyLockerModal/hooks";

import type { KeyLocker } from "models";

type CreateEditKeyLockerModalProps = {
  isOpen: boolean;
  onClose: () => void;
  keyLocker?: KeyLocker | null;
};

export type CreateEditKeyLockerFormValues = Pick<KeyLocker, "name" | "is_2fa_to_drop_key_enabled">;

const FormHelper = createFormHelper<CreateEditKeyLockerFormValues>();

export const CreateEditKeyLockerModal = ({ isOpen, onClose, keyLocker }: CreateEditKeyLockerModalProps) => {
  const { selectedLocation } = useSelectedLocation();
  const [hasErrors, setHasErrors] = useState(false);
  const [showPrivateKeyModal, setShowPrivateKeyModal] = useState(false);

  const { mutateAsync: updateKeyLocker, isPending: isUpdateKeyLockerPending } = useUpdateKeyLockerMutation();
  const { mutateAsync: createKeyLocker, isPending: isCreateKeyLockerPending, data: createdKeyLocker, isSuccess: isCreateKeyLockerSuccess } = useCreateKeyLockerMutation();

  const handleClose = () => {
    onClose();
    setHasErrors(false);
    setShowPrivateKeyModal(false);
  };

  const validateFormData = ({ name }: CreateEditKeyLockerFormValues) => {
    setHasErrors(!name);
    return !name;
  };

  const handleSubmit = async (values: CreateEditKeyLockerFormValues) => {
    if (validateFormData(values)) return;

    if (keyLocker) {
      await updateKeyLocker({ ...values, keylocker_id: keyLocker.id });
    } else {
      if (!selectedLocation?.id) return;
      await createKeyLocker({ ...values, location_id: selectedLocation.id });
    }

    handleClose();
  };

  useEffect(() => {
    if (isCreateKeyLockerSuccess) setShowPrivateKeyModal(true);
  }, [isCreateKeyLockerSuccess]);

  return (
    <>
      <Modal open={isOpen} size="mini">
        <FormHelper.Form
          onSubmit={handleSubmit}
          defaultValues={{ is_2fa_to_drop_key_enabled: keyLocker?.is_2fa_to_drop_key_enabled ?? true, name: keyLocker?.name || "" }}
        >
          <div className="CreateEditKeyLockerModal">
            <CreateEditKeyLockerForm
              hasErrors={hasErrors}
              onClose={handleClose}
              onSubmit={handleSubmit}
              isCreatingEditingKeyLocker={isUpdateKeyLockerPending || isCreateKeyLockerPending}
              isEdit={Boolean(keyLocker)}
            />
          </div>
        </FormHelper.Form>
      </Modal>
      <PrivateKeyModal isOpen={showPrivateKeyModal} onClose={handleClose} privateKey={createdKeyLocker?.private_key} />
    </>
  );
};
